<template>
  <v-card flat>
    <v-row no-gutters style="min-height: 600px">
      <v-col cols="12" md="4" v-if="!isMobile">
        <v-card flat>
          <v-card-subtitle class="subtitle-1 font-weight-bold mb-2 mr-4"
            >{{ $t("components.partner.PartnerReportCreateForm.vehicle") }}
          </v-card-subtitle>

          <v-card outlined class="mx-4 mb-4">
            <refs-vehicle v-if="selectedVehicle" :item="selectedVehicle" @openDetail="goToVehicle" />
            <v-card-text v-else>{{ $t("components.partner.PartnerReportCreateForm.noVehicleSelected") }}</v-card-text>
          </v-card>
        </v-card>

        <v-card class="ma-4" outlined v-if="selectedFleet && selectedFleet.company">
          <company-sheet color="white" :company="selectedFleet.company" />
        </v-card>

        <v-card outlined class="ma-4" v-if="selectedVehicle">
          <v-card-text v-if="!selectedVehicle.groupId">
            <v-row>
              <v-col cols="auto">
                <v-icon class="mr-2" color="error">mdi-alert-circle</v-icon>
              </v-col>
              <v-col>
                {{ $t("components.partner.PartnerReportCreateForm.vehicleNotAssigned") }}
                <div class="text-caption">{{ $t("components.partner.PartnerReportCreateForm.assignFleet") }}</div>
              </v-col>
            </v-row>
          </v-card-text>
          <v-card-text v-else-if="isLoadingFleet">
            <v-skeleton-loader type="list-item-avatar"></v-skeleton-loader>
          </v-card-text>
          <div v-else-if="selectedFleet">
            <refs-fleet :item="selectedFleet" @openDetail="goToFleet" />
          </div>
          <v-card-text v-else>{{ $t("components.partner.PartnerReportCreateForm.noFleetContext") }}</v-card-text>
        </v-card>

        <v-card flat v-if="canReadPerson">
          <v-card-subtitle class="subtitle-1 font-weight-bold mb-2 mr-4">
            {{ $t("components.partner.PartnerReportCreateForm.driver") }}
          </v-card-subtitle>
          <v-card outlined class="mx-4 mb-4">
            <refs-person v-if="selectedPerson" :item="selectedPerson" />
            <v-card-text v-else>{{ $t("components.partner.PartnerReportCreateForm.noDriverSelected") }}</v-card-text>
          </v-card>
        </v-card>

        <v-card flat>
          <v-card-subtitle class="subtitle-1 font-weight-bold mb-2 mr-4">
            {{ $t("components.partner.PartnerReportCreateForm.damage") }}
          </v-card-subtitle>

          <v-card outlined class="mx-4 mb-4">
            <v-list-item v-if="damageType && coverageType">
              <v-list-item-content>
                <v-list-item-title>
                  <v-chip small v-if="drivable" :color="drivable.color">
                    {{ $t(drivable.label) }}
                  </v-chip>
                </v-list-item-title>
                <v-list-item-subtitle>
                  <v-icon x-small class="mr-1">mdi-hammer-wrench</v-icon>
                  <span class="mr-2">{{ $t(`enums.DamageTypeEnum.${damageType}`) }}</span>
                  <v-icon x-small class="mr-1">mdi-shield-outline</v-icon>
                  <span>{{ $t(`enums.CoverageTypeEnum.${coverageType}`) }}</span>
                </v-list-item-subtitle>
              </v-list-item-content>
            </v-list-item>
            <v-card-text v-else>{{ $t("components.partner.PartnerReportCreateForm.noDamageInfo") }}</v-card-text>
          </v-card>
        </v-card>
      </v-col>

      <v-col cols="12" md="8" class="d-flex flex-column left-border-column">
        <v-card flat class="flex-grow-1 d-flex flex-column">
          <v-stepper v-model="currentStep" class="elevation-0 flex-grow-0">
            <v-stepper-header>
              <v-stepper-step
                :complete="stepCompletion.step1"
                :step="CreateReportSteps.VEHICLE"
                editable
                @click="setStep(CreateReportSteps.VEHICLE)"
                complete-icon="mdi-check"
              >
                {{ $t("components.partner.PartnerReportCreateForm.vehicle") }}
                <small v-if="stepCompletion.step1">
                  {{ selectedVehicle ? selectedVehicle.numberplate : numberplate }}
                </small>
              </v-stepper-step>

              <v-divider></v-divider>

              <v-stepper-step
                :complete="stepCompletion.step2"
                :step="CreateReportSteps.DRIVER"
                editable
                @click="setStep(CreateReportSteps.DRIVER)"
                complete-icon="mdi-check"
                v-if="canReadPerson"
              >
                {{ $t("components.partner.PartnerReportCreateForm.driver") }}
                <small v-if="stepCompletion.step2">
                  {{ selectedPerson ? `${selectedPerson.firstName} ${selectedPerson.lastName}` : "" }}
                </small>
              </v-stepper-step>

              <v-divider v-if="canReadPerson"></v-divider>

              <v-stepper-step
                :complete="stepCompletion.step3"
                :step="CreateReportSteps.DAMAGE"
                editable
                @click="setStep(CreateReportSteps.DAMAGE)"
                complete-icon="mdi-check"
              >
                {{ $t("components.partner.PartnerReportCreateForm.damage") }}
                <small v-if="stepCompletion.step3 && damageType">
                  {{ $t(`enums.DamageTypeEnum.${damageType}`) }}
                </small>
              </v-stepper-step>

              <v-divider></v-divider>

              <v-stepper-step
                :complete="stepCompletion.step4"
                :step="CreateReportSteps.CONFIRM"
                editable
                @click="setStep(CreateReportSteps.CONFIRM)"
                complete-icon="mdi-check"
              >
                {{ $t("save") }}
              </v-stepper-step>
            </v-stepper-header>

            <v-stepper-items class="flex-grow-1">
              <!-- Step 1: Fahrzeugdaten -->
              <v-stepper-content :step="CreateReportSteps.VEHICLE">
                <v-card flat>
                  <v-card-title>
                    <span>
                      {{ $t("components.partner.PartnerReportCreateForm.vehicleSearch") }}
                    </span>
                    <v-spacer />
                    <v-btn
                      v-if="canCreateVehicle"
                      color="primary"
                      small
                      elevation="0"
                      @click="isCreateVehicleDialogActive = true"
                      >{{ $t("components.partner.PartnerReportCreateForm.newVehicle") }}</v-btn
                    >
                    <create-vehicle-dialog
                      v-if="canCreateVehicle"
                      :isDialogActive.sync="isCreateVehicleDialogActive"
                      :partnerId="partnerId"
                      :isAutoClose="true"
                      @vehicleCreated="setVehicle"
                    />
                  </v-card-title>
                  <v-card-text>
                    <v-autocomplete
                      v-model="selectedVehicle"
                      focus
                      :items="filteredVehicles"
                      :loading="isSearchingVehicles"
                      :search-input.sync="vehicleSearchPattern"
                      item-text="numberplate"
                      item-value="id"
                      :label="$t('components.partner.PartnerReportCreateForm.vehicle')"
                      :placeholder="$t('components.partner.PartnerReportCreateForm.vehiclePlaceholder')"
                      :color="selectedVehicle ? 'success' : ''"
                      return-object
                      clearable
                      outlined
                      autofocus
                      @input="checkStep1Completion"
                      @change="checkStep1Completion"
                      @update:search-input="debounceVehicleSearch"
                      :hint="$t('components.partner.PartnerReportCreateForm.vehicleSearchHint')"
                      persistent-hint
                      ref="vehicleSearchAutocomplete"
                      @keyup.enter="onEnterVehicleSearch"
                      :no-filter="true"
                    >
                      <template v-slot:no-data>
                        <v-list-item v-if="isSearchingVehicles">
                          <v-list-item-content>
                            <v-list-item-title>
                              {{ $t("components.partner.PartnerReportCreateForm.searchingVehicles") }}
                            </v-list-item-title>
                          </v-list-item-content>
                        </v-list-item>
                        <v-list-item v-else>
                          <v-list-item-content>
                            <v-list-item-title>
                              {{
                                vehicleSearchPattern && vehicleSearchPattern.length >= 2
                                  ? $t("components.partner.PartnerReportCreateForm.noVehiclesFound")
                                  : $t("components.partner.PartnerReportCreateForm.searchForVehicle")
                              }}
                            </v-list-item-title>
                          </v-list-item-content>
                        </v-list-item>
                      </template>
                      <template v-slot:item="{ item }">
                        <refs-vehicle :item="item">
                          <template v-slot:icon>
                            <v-list-item-avatar>
                              <v-icon>mdi-car</v-icon>
                            </v-list-item-avatar>
                          </template>
                        </refs-vehicle>
                      </template>
                      <template v-slot:selection="{ item }">
                        <v-icon small color="success" class="mr-2" v-if="selectedVehicle">mdi-check-circle</v-icon>
                        <span class="pr-2 grey--text text--darken-1">{{ item.displayName || item.numberplate }}</span>
                      </template>
                    </v-autocomplete>
                  </v-card-text>
                </v-card>

                <v-row v-if="isLoadingVehicleContractCompanies" class="px-4" dense>
                  <v-col cols="12" md="6">
                    <v-card outlined>
                      <v-skeleton-loader type="list-item-avatar" />
                    </v-card>
                  </v-col>
                  <v-col cols="12" md="6">
                    <v-card outlined>
                      <v-skeleton-loader type="list-item-avatar" />
                    </v-card>
                  </v-col>
                </v-row>

                <v-row v-else-if="vehicleInsuranceCompany || vehicleLeasingCompany" class="px-4" dense>
                  <v-col cols="12" md="6" v-if="vehicleInsuranceCompany">
                    <v-card outlined>
                      <v-list-item dense>
                        <v-list-item-icon class="mr-3 my-auto">
                          <v-icon color="primary">
                            {{ serviceIconMap(CompanyServiceEnum.INSURANCE) }}
                          </v-icon>
                        </v-list-item-icon>
                        <v-list-item-content class="py-2">
                          <v-list-item-subtitle class="text-caption">
                            {{ $t("enums.VehicleContractTypeEnum.insurance") }}
                          </v-list-item-subtitle>
                          <v-list-item-title class="text-truncate">
                            {{ vehicleInsuranceCompany.companyName }}
                          </v-list-item-title>
                          <v-list-item-subtitle
                            v-if="
                              activeVehicleInsuranceContract &&
                              (activeVehicleInsuranceContract.number || insuranceContractDateRange)
                            "
                            class="text-caption text-truncate"
                          >
                            <a
                              v-if="activeVehicleInsuranceContract.number"
                              @click.stop="goToVehicleContract(activeVehicleInsuranceContract)"
                            >
                              {{ activeVehicleInsuranceContract.number }}
                            </a>
                            <span v-if="insuranceContractDateRange" class="ml-1">
                              {{ insuranceContractDateRange }}
                            </span>
                          </v-list-item-subtitle>
                        </v-list-item-content>
                      </v-list-item>
                    </v-card>
                  </v-col>
                  <v-col cols="12" md="6" v-if="vehicleLeasingCompany">
                    <v-card outlined>
                      <v-list-item dense>
                        <v-list-item-icon class="mr-3 my-auto">
                          <v-icon color="primary">
                            {{ serviceIconMap(CompanyServiceEnum.LEASING) }}
                          </v-icon>
                        </v-list-item-icon>
                        <v-list-item-content class="py-2">
                          <v-list-item-subtitle class="text-caption">
                            {{ $t("enums.VehicleContractTypeEnum.leasing") }}
                          </v-list-item-subtitle>
                          <v-list-item-title class="text-truncate">
                            {{ vehicleLeasingCompany.companyName }}
                          </v-list-item-title>
                          <v-list-item-subtitle
                            v-if="
                              activeVehicleLeasingContract &&
                              (activeVehicleLeasingContract.number || leasingContractDateRange)
                            "
                            class="text-caption text-truncate"
                          >
                            <a
                              v-if="activeVehicleLeasingContract.number"
                              @click.stop="goToVehicleContract(activeVehicleLeasingContract)"
                            >
                              {{ activeVehicleLeasingContract.number }}
                            </a>
                            <span v-if="leasingContractDateRange" class="ml-1">
                              {{ leasingContractDateRange }}
                            </span>
                          </v-list-item-subtitle>
                        </v-list-item-content>
                      </v-list-item>
                    </v-card>
                  </v-col>
                </v-row>

                <v-row v-if="isLoadingFleet" class="px-4" dense>
                  <v-col>
                    <v-card flat outlined>
                      <v-skeleton-loader type="list-item-avatar, paragraph" />
                    </v-card>
                  </v-col>
                </v-row>

                <v-row v-else-if="selectedFleet && selectedFleet.description" class="px-4" dense>
                  <v-col>
                    <v-card flat outlined>
                      <v-card-title class="pa-0">
                        <v-list-item dense>
                          <v-list-item-icon class="mr-3 my-auto">
                            <v-icon color="primary">
                              {{ serviceIconMap(CompanyServiceEnum.FLEET) }}
                            </v-icon>
                          </v-list-item-icon>
                          <v-list-item-content class="py-2">
                            <v-list-item-subtitle class="text-caption">
                              {{ $t("BackendResourceEnum.fleet") }}
                            </v-list-item-subtitle>
                            <v-list-item-title class="text-truncate">
                              {{
                                selectedFleet.company
                                  ? `${selectedFleet.company.companyName} - ${selectedFleet.title}`
                                  : selectedFleet.title
                              }}
                            </v-list-item-title>
                          </v-list-item-content>
                        </v-list-item>
                      </v-card-title>

                      <v-card-text style="max-height: 300px; overflow-y: auto">
                        <markdown-editor
                          :value="selectedFleet.description"
                          :readOnly="true"
                          :outlined="false"
                          :noOverflow="true"
                          dense
                        />
                      </v-card-text>
                    </v-card>
                  </v-col>
                </v-row>
              </v-stepper-content>

              <!-- Step 2: Kontaktdaten -->
              <v-stepper-content :step="CreateReportSteps.DRIVER" v-if="canReadPerson">
                <v-card flat>
                  <v-card-title>
                    <span>
                      {{ $t("components.partner.PartnerReportCreateForm.driverSearch") }}
                    </span>

                    <v-spacer />
                    <v-btn
                      v-if="canCreatePerson"
                      color="primary"
                      small
                      elevation="0"
                      @click="isCreatePersonDialogActive = true"
                      >{{ $t("components.partner.PartnerReportCreateForm.newPerson") }}</v-btn
                    >
                    <person-create-dialog
                      v-if="canCreatePerson"
                      :isDialogActive.sync="isCreatePersonDialogActive"
                      :partnerId="partnerId"
                      @created="setPerson"
                      :hideButton="true"
                    />
                  </v-card-title>
                  <v-card-text>
                    <v-form v-model="isDriverValid">
                      <v-autocomplete
                        v-model="selectedPerson"
                        :items="filteredPersons"
                        :loading="isSearchingPersons"
                        autofocus
                        :search-input.sync="personSearchPattern"
                        item-text="titleReadable"
                        item-value="id"
                        :label="$t('components.partner.PartnerReportCreateForm.driver')"
                        :placeholder="$t('components.partner.PartnerReportCreateForm.driverPlaceholder')"
                        :color="selectedPerson ? 'success' : ''"
                        return-object
                        clearable
                        outlined
                        @input="checkStep2Completion"
                        @change="checkStep2Completion"
                        @update:search-input="debouncePersonSearch"
                        :hint="$t('components.partner.PartnerReportCreateForm.driverSearchHint')"
                        persistent-hint
                        ref="vehiclePersonAutocomplete"
                        :no-filter="true"
                      >
                        <template v-slot:no-data>
                          <v-list-item v-if="isSearchingPersons">
                            <v-list-item-content>
                              <v-list-item-title>
                                {{ $t("components.partner.PartnerReportCreateForm.searchingPersons") }}
                              </v-list-item-title>
                            </v-list-item-content>
                          </v-list-item>
                          <v-list-item v-else>
                            <v-list-item-content>
                              <v-list-item-title>
                                {{
                                  personSearchPattern && personSearchPattern.length >= 2
                                    ? $t("components.partner.PartnerReportCreateForm.noPersonsFound")
                                    : $t("components.partner.PartnerReportCreateForm.searchForPerson")
                                }}
                              </v-list-item-title>
                            </v-list-item-content>
                          </v-list-item>
                        </template>
                        <template v-slot:item="{ item }">
                          <refs-person :item="item">
                            <template v-slot:icon>
                              <v-list-item-avatar>
                                <v-icon>mdi-account</v-icon>
                              </v-list-item-avatar>
                            </template>
                          </refs-person>
                        </template>
                        <template v-slot:selection="{ item }">
                          <v-icon small color="success" class="mr-2" v-if="selectedPerson">mdi-check-circle</v-icon>
                          <span class="pr-2">{{ item.firstName }} {{ item.lastName }}</span>
                        </template>
                      </v-autocomplete>

                      <v-autocomplete
                        outlined
                        v-if="selectedPerson"
                        :items="selectedPerson.emails"
                        v-model="mail"
                        item-text="email"
                        :label="$t('common.nouns.email')"
                        :rules="requiredRule"
                        :search-input.sync="mailSearch"
                        :loading="isMailEditLoading"
                        :disabled="isMailEditLoading"
                        return-object
                        @keydown.enter.native.prevent="onMailEnter"
                      >
                        <template v-slot:item="{ item, attrs, on }">
                          <v-list-item v-bind="attrs" v-on="on">
                            <v-list-item-content>
                              <v-list-item-title>{{ item.email }}</v-list-item-title>
                            </v-list-item-content>

                            <v-list-item-action v-if="canUpdatePerson">
                              <v-btn icon small @click.stop="startUpdateEmail(item)">
                                <v-icon small>mdi-pencil</v-icon>
                              </v-btn>
                            </v-list-item-action>
                          </v-list-item>
                        </template>
                        <template v-slot:no-data v-if="canUpdatePerson">
                          <v-list-item @click="startCreateEmail">
                            <v-list-item-action>
                              <v-icon small>mdi-plus</v-icon>
                            </v-list-item-action>
                            <v-list-item-content>
                              <v-list-item-title>
                                {{ $t("components.partner.PersonEmailDialog.title") }}
                              </v-list-item-title>
                            </v-list-item-content>
                          </v-list-item>
                        </template>
                        <template v-slot:selection="{ item }">
                          <v-icon small color="success" class="mr-2" v-if="item">mdi-check-circle</v-icon>
                          <span class="pr-2" v-if="item.email">{{ item.email }}</span>
                        </template>
                      </v-autocomplete>

                      <person-email-dialog
                        v-model="mailToEdit"
                        :isDialogActive.sync="isCreateMailDialogActive"
                        @save="addEmail"
                      />
                      <person-email-dialog
                        v-model="mailToEdit"
                        :isDialogActive.sync="isUpdateMailDialogActive"
                        @save="updateEmail"
                        title="update"
                      />

                      <v-autocomplete
                        outlined
                        v-if="selectedPerson"
                        :items="selectedPerson.phones"
                        v-model="phone"
                        item-text="phone"
                        :label="$t('common.nouns.phone')"
                        :search-input.sync="phoneSearch"
                        :loading="isPhoneEditLoading"
                        :disabled="isPhoneEditLoading"
                        return-object
                        @keydown.enter.native.prevent="onPhoneEnter"
                      >
                        <template v-slot:item="{ item, attrs, on }">
                          <v-list-item v-bind="attrs" v-on="on">
                            <v-list-item-content>
                              <v-list-item-title>{{ item.phone }}</v-list-item-title>
                            </v-list-item-content>

                            <v-list-item-action v-if="canUpdatePerson">
                              <v-btn icon small @click.stop="startUpdatePhone(item)">
                                <v-icon small>mdi-pencil</v-icon>
                              </v-btn>
                            </v-list-item-action>
                          </v-list-item>
                        </template>
                        <template v-slot:no-data v-if="canUpdatePerson">
                          <v-list-item @click="startCreatePhone">
                            <v-list-item-action>
                              <v-icon small>mdi-plus</v-icon>
                            </v-list-item-action>
                            <v-list-item-content>
                              <v-list-item-title>
                                {{ $t("components.partner.PersonPhoneDialog.title") }}
                              </v-list-item-title>
                            </v-list-item-content>
                          </v-list-item>
                        </template>

                        <template v-slot:selection="{ item }">
                          <v-icon small color="success" class="mr-2" v-if="item">mdi-check-circle</v-icon>
                          <span class="pr-2" v-if="item.phone">{{ item.phone }}</span>
                        </template>
                      </v-autocomplete>

                      <person-phone-dialog
                        v-model="phoneToEdit"
                        :isDialogActive.sync="isCreatePhoneDialogActive"
                        @save="addPhone"
                      />
                      <person-phone-dialog
                        v-model="phoneToEdit"
                        :isDialogActive.sync="isUpdatePhoneDialogActive"
                        @save="updatePhone"
                        title="update"
                      />
                    </v-form>
                  </v-card-text>
                </v-card>
              </v-stepper-content>

              <!-- Step 3: Schadensart -->
              <v-stepper-content :step="CreateReportSteps.DAMAGE">
                <v-card flat>
                  <v-card-title>{{ $t("components.partner.PartnerReportCreateForm.damage") }}</v-card-title>
                  <v-card-text>
                    <v-form v-model="isDamageValid">
                      <v-row>
                        <v-col cols="12" md="6">
                          <custom-field-boolean
                            v-model="isDrivable"
                            :customField="{ label: $t('objects.report.isDrivable') }"
                          />
                        </v-col>
                        <v-col cols="12" md="6">
                          <custom-field-boolean
                            v-model="isRoadworthy"
                            :customField="{ label: $t('objects.report.isRoadworthy') }"
                          />
                        </v-col>
                        <v-col cols="12" md="6">
                          <v-autocomplete
                            v-model="damageType"
                            :items="damageTypeItems"
                            :label="$t('components.partner.PartnerReportCreateForm.damageTypeLabel')"
                            outlined
                            autofocus
                            @change="checkStep3Completion"
                            :filter="customFilter"
                            item-text="text"
                            item-value="value"
                            :return-object="false"
                            :rules="requiredRule"
                            clearable
                          >
                          </v-autocomplete>
                        </v-col>
                        <v-col cols="12" md="6">
                          <v-autocomplete
                            v-model="coverageType"
                            :items="coverageTypeItems"
                            :label="$t('components.partner.PartnerReportCreateForm.coverageTypeLabel')"
                            outlined
                            @change="checkStep3Completion"
                            :filter="customFilter"
                            item-text="text"
                            item-value="value"
                            :return-object="false"
                            :rules="requiredRule"
                            clearable
                          >
                            <template v-slot:selection="{ item }">
                              {{ item.text }}
                            </template>
                            <template v-slot:item="{ item }">
                              {{ item.text }}
                            </template>
                          </v-autocomplete>
                        </v-col>
                        <v-col cols="12">
                          <v-textarea
                            :label="$t('common.nouns.description')"
                            flat
                            outlined
                            v-model="message"
                            :rules="requiredRule"
                          />
                        </v-col>
                      </v-row>
                    </v-form>
                  </v-card-text>
                </v-card>
              </v-stepper-content>

              <v-stepper-content :step="CreateReportSteps.CONFIRM">
                <v-card flat>
                  <v-card-title>{{ $t("components.partner.PartnerReportCreateForm.nextSteps") }}</v-card-title>
                  <v-card-subtitle>{{
                    $t("components.partner.PartnerReportCreateForm.checkSummaryBeforeCreation")
                  }}</v-card-subtitle>
                  <v-card-text v-if="canReadPerson">
                    <v-checkbox
                      v-if="hasEmail || hasPhone"
                      v-model="isNotifyUser"
                      :label="$t('components.partner.PartnerReportCreateForm.notifyUserByEmail')"
                    >
                    </v-checkbox>
                    <v-radio-group
                      v-if="isNotifyUser && hasEmail && hasPhone"
                      v-model="selectedChannel"
                      row
                      class="mt-0 ml-8"
                      hide-details
                    >
                      <v-radio
                        :label="$t('components.partner.PartnerReportCreateForm.channelEmail')"
                        :value="ConversationChannelEnum.EMAIL"
                      ></v-radio>
                      <v-radio
                        :label="$t('components.partner.PartnerReportCreateForm.channelSms')"
                        :value="ConversationChannelEnum.SMS"
                      ></v-radio>
                    </v-radio-group>
                    <v-checkbox
                      v-model="isShared"
                      :label="$t('components.partner.PartnerReportCreateForm.shareWithDriver')"
                    >
                    </v-checkbox>
                    <v-checkbox
                      v-model="isAttachmentRequest"
                      :label="$t('components.partner.PartnerReportCreateForm.requestDamageDocumentation')"
                    >
                    </v-checkbox>
                    <v-autocomplete
                      v-if="isAttachmentRequest"
                      v-model="screens"
                      :items="selectableScreenItems"
                      :label="$t('components.partner.PartnerReportCreateForm.damageDocumentation')"
                      multiple
                      chips
                      item-text="text"
                      item-value="value"
                      :return-object="false"
                      :filter="customFilter"
                      :hint="$t('components.partner.PartnerReportCreateForm.whatShouldDriverSubmit')"
                      persistent-hint
                      clearable
                      outlined
                    >
                      <template v-slot:selection="{ attrs, item, selected }">
                        <v-chip v-bind="attrs" :input-value="selected" close @click:close="removeScreen(item.value)">
                          {{ item.text }}
                        </v-chip>
                      </template>
                    </v-autocomplete>
                  </v-card-text>
                </v-card>
              </v-stepper-content>
            </v-stepper-items>
          </v-stepper>
          <v-card-actions class="mt-auto pa-4">
            <v-spacer></v-spacer>
            <v-btn text @click="prev" v-if="currentStep > 1">
              {{ currentStep > 1 ? $t("back") : $t("cancel") }}
            </v-btn>

            <v-btn v-if="currentStep < 4" color="primary" :disabled="!canProceedToNextStep" elevation="0" @click="next">
              {{ $t("next") }}
            </v-btn>
            <v-btn v-else color="success" :loading="loading" @click="submitReport" elevation="0">
              {{ $t("components.partner.PartnerReportCreateForm.createDamageReport") }}
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-col>
    </v-row>
  </v-card>
</template>

<script lang="ts">
import CustomFieldBoolean from "@/components/report/CustomFieldBoolean.vue";
import MHeader from "@/components/utility/mmmint/MHeader.vue";
import RefsFleet from "@/components/utility/RefsFleet.vue";
import RefsPerson from "@/components/utility/RefsPerson.vue";
import RefsVehicle from "@/components/utility/RefsVehicle.vue";
import TheLayoutPortal from "@/layouts/TheLayoutPortal.vue";
import { CreateReportAsPartnerDto } from "@/lib/dto/create-report-as-partner-dto";
import { ActivityTypeEnum } from "@/lib/enum/activity-type.enum";
import { VehicleStateEnum } from "@/lib/enum/vehicleState.enum";
import { requiredRule } from "@/lib/rules/requiredRule";
import { handleError } from "@/lib/utility/handleError";
import { parsePhone } from "@/lib/utility/parsePhone";
import PermissionMixin from "@/mixins/PermissionMixin.vue";
import { ActivityLog } from "@/models/activity-log.entity";
import { AttachmentRequest, IAttachmentRequest } from "@/models/attachment-request.entity";
import { Company, CompanyBase, ICompany } from "@/models/company.entity";
import { CreateSharedContentDto } from "@/models/create-shared-content.dto";
import { Fleet, IFleet } from "@/models/fleet.entity";
import { IPersonEmail, PersonEmail } from "@/models/person-email.entity";
import { IPersonPhone, PersonPhone } from "@/models/person-phone.entity";
import { IPerson } from "@/models/person.entity";
import { IReport } from "@/models/report.entity";
import { ISharedContent } from "@/models/shared-content.entity";
import { IVehicle, Vehicle } from "@/models/vehicle.entity";
import { ActionEnum } from "@/store/enum/authActionEnum";
import { BackendResourceEnum, ResourceEnum } from "@/store/enum/authResourceEnum";
import { CoverageTypeEnum } from "@/store/enum/coverage-type.enum";
import { DamageTypeEnum } from "@/store/enum/damage-type.enum";
import { ReportScreenEnum } from "@/store/enum/partner/report-screen.enum";
import { DamageTypeToCoverageTypeMap } from "@/store/mapper/damage-type-to-coverage-type.map";
import { UserModule } from "@/store/modules/me-user.store";
import { PartnerModule } from "@/store/modules/partner";
import { RefTypeMap } from "@/store/modules/refs.store";
import { PersonSearchModule, VehicleSearchModule } from "@/store/modules/search.store";
import { VehicleModule } from "@/store/modules/vehicle.store";
import { debounce } from "debounce";
import { mixins } from "vue-class-component";
import { Component, Prop, Vue, Watch } from "vue-property-decorator";
import CompanySheet from "../company/CompanySheet.vue";
import MarkdownEditor from "@/components/utility/MarkdownEditor.vue";
import { simpleDoubleDigitDate } from "@/lib/utility/date-helper";
import { GoToHelper } from "@/lib/utility/goToHelper";
import { CompanyGoToHelper } from "@/lib/utility/company.go-to-helper";
import CreateVehicleDialog from "../fleet/CreateVehicleDialog.vue";
import PersonCreateDialog from "../person/PersonCreateDialog.vue";
import PersonEmailDialog from "../person/PersonEmailDialog.vue";
import PersonPhoneDialog from "../person/PersonPhoneDialog.vue";
import { CompanyServiceEnum } from "@/lib/enum/company-service.enum";
import { ConversationChannelEnum } from "@/lib/enum/conversation-channel.enum";

enum CreateReportSteps {
  VEHICLE = 1,
  DRIVER = 2,
  DAMAGE = 3,
  CONFIRM = 4
}

export type PartnerReportCreateFormSubmit = {
  report: IReport;
  sharedContent?: ISharedContent;
  attachmentRequest?: IAttachmentRequest;
  isNotifyUser: boolean;
  isShared: boolean;
  isAttachmentRequest: boolean;
  selectedChannel: ConversationChannelEnum;
};

@Component({
  components: {
    TheLayoutPortal,
    MHeader,
    RefsPerson,
    RefsVehicle,
    RefsFleet,
    CustomFieldBoolean,
    CreateVehicleDialog,
    PersonCreateDialog,
    CompanySheet,
    PersonEmailDialog,
    PersonPhoneDialog,
    MarkdownEditor
  }
})
export default class PartnerReportCreateForm extends mixins(Vue, PermissionMixin) {
  DamageTypeEnum = DamageTypeEnum;
  CoverageTypeEnum = CoverageTypeEnum;
  CreateReportSteps = CreateReportSteps;
  ConversationChannelEnum = ConversationChannelEnum;
  CompanyServiceEnum = CompanyServiceEnum;

  isDamageValid = false;
  isDriverValid = false;

  @Prop({ required: false })
  groupId?: string;

  @Prop({ required: false })
  vehicle?: IVehicle;

  loading = false;

  isCreateVehicleDialogActive = false;
  isCreatePersonDialogActive = false;
  isCreateMailDialogActive = false;
  isUpdateMailDialogActive = false;
  isCreatePhoneDialogActive = false;
  isUpdatePhoneDialogActive = false;

  // #region data, non-ui
  currentStep = CreateReportSteps.VEHICLE;
  numberplate = "";

  // Change the type definitions to be more flexible
  damageType: DamageTypeEnum | null = null;
  coverageType: CoverageTypeEnum | null = null;
  message: string | null = null;
  isDrivable = true;
  isRoadworthy = true;

  // Vehicle search properties
  vehicleSearchPattern = "";
  isSearchingVehicles = false;
  selectedVehicle: IVehicle | null = this.vehicle || null;

  selectedFleet: IFleet | null = null;
  vehicleInsuranceCompany: ICompany | null = null;
  vehicleLeasingCompany: ICompany | null = null;
  isLoadingVehicleContractCompanies = false;

  // Person search properties
  personSearchPattern = "";
  isSearchingPersons = false;
  selectedPerson: IPerson | null = null;

  debounceTimeout = 300;

  phoneSearch = "";
  phone: IPersonPhone | null = null;

  phoneToEdit: IPersonPhone = new PersonPhone();
  phoneUpdateIndex: number | null = null;
  isPhoneEditLoading = false;

  mailSearch = "";
  mail: IPersonEmail | null = null;

  mailToEdit: IPersonEmail = new PersonEmail();
  mailUpdateIndex: number | null = null;
  isMailEditLoading = false;

  isFleetDescriptionExpanded = false;

  startCreateEmail() {
    this.mailToEdit = new PersonEmail({ email: this.mailSearch });
    this.isCreateMailDialogActive = true;
  }

  startUpdateEmail(item: IPersonEmail) {
    const idx =
      this.selectedPerson?.emails.findIndex(
        (e) => (e.email || "").toLowerCase() === (item.email || "").toLowerCase()
      ) ?? -1;

    if (idx < 0) return;

    this.mailUpdateIndex = idx;

    this.mailToEdit = new PersonEmail({ ...this.selectedPerson?.emails[idx] });
    this.isUpdateMailDialogActive = true;
  }

  async updateEmail(updated: IPersonEmail) {
    if (this.mailUpdateIndex == null || !this.selectedPerson) return;

    this.isMailEditLoading = true;
    try {
      this.$set(this.selectedPerson.emails, this.mailUpdateIndex, updated);

      await this.selectedPerson.update();
      this.mail = this.selectedPerson.emails[this.mailUpdateIndex] || null;
    } catch (e) {
      handleError(e);
    } finally {
      this.isMailEditLoading = false;
    }
  }

  findExistingEmail(str: string) {
    const s = (str || "").trim().toLowerCase();

    return this.selectedPerson?.emails?.find((e) => (e.email || "").toLowerCase() === s) || null;
  }

  onMailEnter() {
    const s = (this.mailSearch || "").trim();
    if (!s) return;

    const existing = this.findExistingEmail(s);
    if (existing) {
      this.mail = existing;

      return;
    }

    this.startCreateEmail();
  }

  findExistingPhone(str: string) {
    const s = parsePhone(str || "");

    return this.selectedPerson?.phones?.find((e) => parsePhone(e.phone || "") === s) || null;
  }

  startUpdatePhone(item: IPersonPhone) {
    const idx =
      this.selectedPerson?.phones.findIndex(
        (e) => (e.phone || "").toLowerCase() === (item.phone || "").toLowerCase()
      ) ?? -1;

    if (idx < 0) return;

    this.phoneUpdateIndex = idx;

    this.phoneToEdit = new PersonPhone({ ...this.selectedPerson?.phones[idx] });
    this.phoneToEdit.phone = parsePhone(this.selectedPerson?.phones[idx]?.phone || "");
    this.isUpdatePhoneDialogActive = true;
  }

  async updatePhone(updated: IPersonPhone) {
    if (this.phoneUpdateIndex == null || !this.selectedPerson) return;

    this.isPhoneEditLoading = true;
    try {
      this.$set(this.selectedPerson.phones, this.phoneUpdateIndex, updated);

      await this.selectedPerson.update();
      this.phone = this.selectedPerson.phones[this.phoneUpdateIndex] || null;
    } catch (e) {
      handleError(e);
    } finally {
      this.isPhoneEditLoading = false;
    }
  }

  onPhoneEnter() {
    const s = (this.phoneSearch || "").trim();
    if (!s) return;

    const existing = this.findExistingPhone(s);
    if (existing) {
      this.phone = existing;

      return;
    }

    this.startCreatePhone();
  }

  startCreatePhone() {
    this.phoneToEdit = new PersonPhone();
    this.phoneToEdit.phone = this.phoneSearch;
    this.isCreatePhoneDialogActive = true;
  }

  async addPhone() {
    this.isPhoneEditLoading = true;

    try {
      this.selectedPerson?.phones.unshift(this.phoneToEdit);
      await this.selectedPerson?.update();

      this.phone = this.selectedPerson?.phones[0] || null;
    } catch (error) {
      handleError(error);
    } finally {
      this.isPhoneEditLoading = false;
    }
  }

  async addEmail() {
    this.isMailEditLoading = true;

    try {
      this.selectedPerson?.emails.unshift(this.mailToEdit);
      await this.selectedPerson?.update();

      this.mail = this.selectedPerson?.emails[0] || null;
    } catch (error) {
      handleError(error);
    } finally {
      this.isMailEditLoading = false;
    }
  }

  isNotifyUser = true;
  isShared = true;
  isAttachmentRequest = true;
  isMapRequest = false;
  selectedChannel: ConversationChannelEnum = ConversationChannelEnum.EMAIL;

  screens: ReportScreenEnum[] = this.selectableScreens;

  get hasEmail(): boolean {
    return !!this.mail?.email;
  }

  get hasPhone(): boolean {
    return !!this.phone?.phone;
  }

  @Watch("hasEmail")
  @Watch("hasPhone")
  onContactAvailabilityChange() {
    if (this.selectedChannel === ConversationChannelEnum.EMAIL && !this.hasEmail && this.hasPhone) {
      this.selectedChannel = ConversationChannelEnum.SMS;
    } else if (this.selectedChannel === ConversationChannelEnum.SMS && !this.hasPhone && this.hasEmail) {
      this.selectedChannel = ConversationChannelEnum.EMAIL;
    }
  }

  get BackendResourceEnum() {
    return BackendResourceEnum;
  }

  get RefTypeMap() {
    return RefTypeMap;
  }

  get stateMap() {
    return VehicleModule.stateMap;
  }

  get drivable() {
    return this.isDrivable ? this.stateMap.get(VehicleStateEnum.ACTIVE) : this.stateMap.get(VehicleStateEnum.INACTIVE);
  }

  stepCompletion = {
    step1: false,
    step2: false,
    step3: false,
    step4: false
  };

  get partnerId() {
    return this.partner?._id || this.partner.id;
  }

  get partner() {
    return PartnerModule.partner;
  }

  get filteredVehicles() {
    return VehicleSearchModule.filtered || [];
  }

  get filteredPersons() {
    return PersonSearchModule.filtered || [];
  }

  get requiredRule() {
    return [requiredRule()];
  }

  get userId() {
    return UserModule.userId;
  }

  get isMobile() {
    return this.$vuetify.breakpoint.smAndDown;
  }

  get selectableScreens() {
    return this.removeNonSelectableScreens(
      (PartnerModule.partner.settings?.reportSettings.screenOrder ?? []) as ReportScreenEnum[]
    );
  }

  get selectableScreenItems() {
    return this.selectableScreens.map((value) => ({
      value,
      text: this.$t(`screenEnum.${value}.title`)
    }));
  }

  @Watch("vehicle")
  setSelectedVehicle() {
    if (this.selectedVehicle?.id !== this.vehicle?.id) {
      this.selectedVehicle = this.vehicle || null;
      this.checkStep1Completion();
    }
  }

  @Watch("selectedVehicle")
  emitSelectVehicle() {
    if (this.selectedVehicle?.id !== this.vehicle?.id) {
      this.$emit("update:vehicle", this.selectedVehicle);
    }
    this.checkStep1Completion();
  }

  removeNonSelectableScreens(screens: ReportScreenEnum[]) {
    return screens.filter((screen) => !AttachmentRequest.INVALID_SCREENS.includes(screen));
  }

  setVehicle(vehicle: IVehicle) {
    this.$log.debug("Set new vehicle", vehicle);
    this.selectedVehicle = vehicle;
    this.numberplate = vehicle.numberplate || "";

    this.checkStep1Completion();
  }

  private isApplyingFetchedVehicle = false;

  async fetchSelectedVehicle() {
    if (!this.canReadVehicle || !this.canReadContract) return;

    const vehicleId = this.selectedVehicle?.id;
    const partnerId = this.partnerId || this.selectedVehicle?.partnerId;

    if (!vehicleId || !partnerId) return;

    try {
      const fetchedVehicle = await new Vehicle({ id: vehicleId, partnerId }).fetch();

      if (this.selectedVehicle?.id !== vehicleId) return;

      this.isApplyingFetchedVehicle = true;
      this.selectedVehicle = fetchedVehicle;
      await this.$nextTick();
      this.isApplyingFetchedVehicle = false;

      await this.fetchVehicleContractCompanies();
    } catch (error) {
      this.isApplyingFetchedVehicle = false;
      this.$log.error("Failed to fetch vehicle contracts", error);
    }
  }

  get activeVehicleInsuranceContract() {
    return this.selectedVehicle?.activeInsuranceContract?.() ?? null;
  }

  get activeVehicleLeasingContract() {
    return this.selectedVehicle?.activeLeasingContract?.() ?? null;
  }

  get insuranceContractDateRange() {
    return this.formatContractDateRange(this.activeVehicleInsuranceContract);
  }

  get leasingContractDateRange() {
    return this.formatContractDateRange(this.activeVehicleLeasingContract);
  }

  formatContractDateRange(contract: { startDate?: string | null; endDate?: string | null } | null) {
    if (!contract) return "";
    const start = contract.startDate ? simpleDoubleDigitDate(contract.startDate) : "";
    const end = contract.endDate ? simpleDoubleDigitDate(contract.endDate) : "";
    if (!start && !end) return "";
    if (!start) return end;
    if (!end) return start;
    return `${start} – ${end}`;
  }

  goToVehicleContract(contract: { id?: string | null } | null) {
    if (!contract?.id || !this.selectedVehicle?.id) return;
    new GoToHelper(this.$router).goToContractDetail(this.partnerId, this.selectedVehicle.id, contract.id, true);
  }

  async fetchVehicleContractCompanies() {
    this.vehicleInsuranceCompany = null;
    this.vehicleLeasingCompany = null;

    if (!this.canReadCompany) return;

    const vehicle = this.selectedVehicle;
    if (!vehicle) return;

    this.isLoadingVehicleContractCompanies = true;
    try {
      await this.loadVehicleContractCompanies(vehicle);
    } finally {
      this.isLoadingVehicleContractCompanies = false;
    }
  }

  private async loadVehicleContractCompanies(vehicle: IVehicle) {
    const insuranceContract = vehicle.activeInsuranceContract?.();
    const leasingContract = vehicle.activeLeasingContract?.();

    const fetchInsuranceCompany = insuranceContract?.companyId
      ? new Company({ id: insuranceContract.companyId, partnerId: vehicle.partnerId }).fetch().catch((error) => {
          this.$log.error("Failed to fetch vehicle insurance company", error);
          return null;
        })
      : Promise.resolve(null);

    const fetchLeasingCompany = leasingContract?.companyId
      ? new Company({ id: leasingContract.companyId, partnerId: vehicle.partnerId }).fetch().catch((error) => {
          this.$log.error("Failed to fetch vehicle leasing company", error);
          return null;
        })
      : Promise.resolve(null);

    const [insuranceCompany, leasingCompany] = await Promise.all([fetchInsuranceCompany, fetchLeasingCompany]);

    this.vehicleInsuranceCompany = insuranceCompany;
    this.vehicleLeasingCompany = leasingCompany;
  }

  setPerson(person: IPerson) {
    this.$log.debug("Set new person", person);
    this.selectedPerson = person;

    this.checkStep2Completion();
  }

  mounted() {
    this.$nextTick(() => {
      const autocompleteElement = this.$refs.vehicleSearchAutocomplete as HTMLInputElement;
      if (autocompleteElement) {
        autocompleteElement.focus();
      }
    });
    this.checkStep1Completion();
  }

  removeScreen(item: ReportScreenEnum) {
    this.screens.splice(this.screens.indexOf(item), 1);
  }

  onEnterVehicleSearch() {
    if (this.selectedVehicle) {
      this.setStep(2);
    }
  }

  onEnterPersonSearch() {
    if (this.selectedPerson) {
      this.setStep(3);
    }
  }

  // Methods for step completion checks
  checkStep1Completion() {
    this.stepCompletion.step1 = Boolean(this.selectedVehicle);
  }

  checkStep2Completion() {
    this.stepCompletion.step2 = Boolean(this.selectedPerson);
  }

  // Update step completion check for step 3
  checkStep3Completion() {
    this.stepCompletion.step3 = Boolean(this.damageType) && Boolean(this.coverageType);
  }

  prev() {
    const nextStep = this.currentStep - 1;

    if (nextStep == CreateReportSteps.DRIVER && !this.canReadPerson) {
      return this.setStep(CreateReportSteps.VEHICLE);
    }

    this.setStep(nextStep);
  }

  next() {
    const nextStep = this.currentStep + 1;

    if (nextStep == CreateReportSteps.DRIVER && !this.canReadPerson) {
      return this.setStep(CreateReportSteps.DAMAGE);
    }

    this.setStep(nextStep);
  }

  // Method to handle step changes
  setStep(step: number) {
    this.currentStep = step;
  }

  // Vehicle search methods
  debounceVehicleSearch = debounce((val: string) => {
    this.vehicleSearchPattern = val;

    // Only perform backend search if we have at least 2 characters
    if (val && val.length >= 2) {
      this.searchVehicles();
    }
  }, this.debounceTimeout);

  async searchVehicles() {
    this.isSearchingVehicles = true;

    try {
      if (UserModule.abilities.can(ActionEnum.READ, ResourceEnum.VEHICLE, this.partnerId)) {
        VehicleSearchModule.setSearch(this.vehicleSearchPattern);
        await VehicleSearchModule.fetchPage({ partnerId: this.partnerId });
      }
    } catch (error) {
      this.$toast?.error && this.$toast.error("Fehler beim Laden der Fahrzeuge");
      this.$log.error("Error searching vehicles:", error);
    } finally {
      this.isSearchingVehicles = false;
    }
  }

  // Person search methods
  debouncePersonSearch = debounce((val: string) => {
    this.personSearchPattern = val;
    this.searchPersons();
  }, this.debounceTimeout);

  async searchPersons() {
    // Only search if we have at least 2 characters
    if (!this.personSearchPattern || this.personSearchPattern.length < 2) {
      return;
    }

    this.isSearchingPersons = true;

    try {
      if (UserModule.abilities.can(ActionEnum.READ, ResourceEnum.PERSON, this.partnerId)) {
        PersonSearchModule.setSearch(this.personSearchPattern);
        await PersonSearchModule.fetchPage({ partnerId: this.partnerId });
      }
    } catch (error) {
      this.$toast?.error && this.$toast.error("Fehler beim Laden der Personen");
      this.$log.error("Error searching persons:", error);
    } finally {
      this.isSearchingPersons = false;
    }
  }

  isLoadingFleet = false;

  async searchFleet() {
    if (!this.canReadFleet) {
      return;
    }
    if (this.selectedVehicle?.groupId) {
      try {
        this.isLoadingFleet = true;
        const fleet = new Fleet({ id: this.selectedVehicle.groupId, partnerId: this.partnerId });

        await fleet.fetch().catch((e) => handleError(e));
        if (this.canReadCompany) {
          await fleet.fetchNextRootCompany().catch((e) => handleError(e));
          await fleet.fetchCompanies().catch((e) => handleError(e));
        }

        return fleet;
      } catch (e) {
        handleError(e);
      } finally {
        this.isLoadingFleet = false;
      }
    }
  }

  /**
   * Immediate, because the dialog hands over the vehicle of the page it was opened from as the initial value.
   */
  @Watch("selectedVehicle", { immediate: true })
  async watchSelectedVehicle() {
    if (this.isApplyingFetchedVehicle) return;

    if (!this.selectedVehicle) {
      this.selectedFleet = null;
      this.vehicleInsuranceCompany = null;
      this.vehicleLeasingCompany = null;
      return;
    }

    const fleet = await this.searchFleet();
    if (fleet) {
      this.selectedFleet = fleet;
    } else {
      this.selectedFleet = null;
      this.$log.debug(`no fleet found ${this.selectedVehicle}`);
    }

    await this.fetchSelectedVehicle();
  }

  @Watch("selectedPerson")
  async watchSelectedPerson() {
    if (this.selectedPerson) {
      if (!this.selectedPerson.emails.length) {
        this.mail = null;
      } else if (this.selectedPerson.emails.length > 0) {
        this.mail = this.selectedPerson.emails[0];
      }
      if (!this.selectedPerson.phones.length) {
        this.phone = null;
      } else if (this.selectedPerson.phones.length > 0) {
        this.phone = this.selectedPerson.phones[0];
      }
    }
  }

  @Watch("damageType")
  watchDamageType() {
    if (this.damageType && !this.coverageType) {
      // Get corresponding coverage type enum value
      const coverageTypeEnum = DamageTypeToCoverageTypeMap.get(this.damageType) ?? null;

      if (coverageTypeEnum) {
        // Create a matching object structure for the coverageType
        this.coverageType = coverageTypeEnum;
      }

      this.checkStep3Completion();
    }
  }

  async submitReport() {
    const partnerId = this.partnerId;

    if (!this.selectedVehicle) {
      this.$toast.error(this.$t("components.partner.PartnerReportCreateForm.errors.selectVehicle"));

      throw new Error("No vehicle");
    }

    if (this.canReadPerson && !this.selectedPerson) {
      this.$toast.error(this.$t("components.partner.PartnerReportCreateForm.errors.selectPerson"));
      throw new Error("No person");
    }

    if (this.canReadPerson && !this.mail) {
      this.$toast.error(this.$t("components.partner.PartnerReportCreateForm.errors.email"));
      throw new Error("No Mail");
    }

    this.loading = true;

    // the insurer and lessor come from the vehicle's contracts, which may have changed since it was selected
    await this.fetchSelectedVehicle();

    const reportDto = CreateReportAsPartnerDto.createFromVehicle(this.selectedVehicle);
    reportDto.firstName = this.selectedPerson?.firstName;
    reportDto.lastName = this.selectedPerson?.lastName;

    if (this.canReadPerson) {
      reportDto.customerContact = {
        email: this.mail?.email || "",
        phone: this.phone?.phone || ""
      };
    } else {
      reportDto.customerContact = undefined;
    }

    reportDto.damageType = this.damageType ?? undefined;

    reportDto.coverageType = this.coverageType ?? undefined;

    reportDto.message = this.message ?? undefined;
    reportDto.isDrivable = this.isDrivable ?? undefined;
    reportDto.isRoadworthy = this.isRoadworthy;

    reportDto.assignees = [this.userId];
    reportDto.personId = this.selectedPerson?.id;

    /**
     * set companies
     *
     * insurer and lessor are the companies of the vehicle's active contracts, as shown in the vehicle step,
     * the fleet's companies otherwise
     */
    reportDto.companyId = this.selectedFleet?.company?.id || this.selectedFleet?.companyId;
    reportDto.companyName = this.selectedFleet?.company?.companyName;

    const insuranceContract = this.activeVehicleInsuranceContract;
    const leasingContract = this.activeVehicleLeasingContract;

    reportDto.insuranceCompanyId =
      insuranceContract?.companyId ||
      this.selectedFleet?.getCompanyByServiceEnum(CompanyServiceEnum.INSURANCE)?.id ||
      undefined;

    reportDto.leasingCompanyId =
      leasingContract?.companyId ||
      this.selectedFleet?.getCompanyByServiceEnum(CompanyServiceEnum.LEASING)?.id ||
      undefined;

    reportDto.insuranceBrokerCompanyId = this.selectedFleet?.getCompanyByServiceEnum(
      CompanyServiceEnum.INSURANCE_BROKER
    )?.id;
    reportDto.attorneyCompanyId = this.selectedFleet?.getCompanyByServiceEnum(CompanyServiceEnum.LAWYER)?.id;

    const insuranceCompany = this.vehicleInsuranceCompany;
    if (insuranceContract && insuranceCompany?.id === insuranceContract.companyId && insuranceCompany.companyName) {
      reportDto.insurances = [
        {
          name: insuranceCompany.companyName,
          number: ""
        }
      ];
    }

    const leasingCompany = this.vehicleLeasingCompany;
    if (leasingContract && leasingCompany?.id === leasingContract.companyId && leasingCompany.companyName) {
      reportDto.leasing = {
        isLeasing: true,
        company: leasingCompany.companyName,
        claimNumber: ""
      };
    }

    reportDto.groupId = this.groupId;

    try {
      const created = await reportDto.create(partnerId);
      created.person = this.selectedPerson || null;

      this.$emit("created", created);

      const submit: PartnerReportCreateFormSubmit = {
        report: created,
        isNotifyUser: this.canReadPerson ? this.isNotifyUser : false,
        isShared: this.canReadPerson ? this.isShared : false,
        isAttachmentRequest: this.canReadPerson ? this.isAttachmentRequest : false,
        selectedChannel: this.selectedChannel
      };

      if (this.isShared) {
        const createSharedContentDto = new CreateSharedContentDto();
        createSharedContentDto.reportId = created.id;
        createSharedContentDto.title = `${this.$t("sharedContent.sharedContent").toString()} ${created.claimNumber} ${
          created.title
        }`;

        if (this.selectedPerson?.id) {
          createSharedContentDto.personIds?.push(this.selectedPerson?.id);
        }

        if (this.mail?.email) {
          createSharedContentDto.emailAddresses?.push(this.mail.email);
        }

        createSharedContentDto.refs = [
          {
            refType: BackendResourceEnum.REPORT,
            refId: created._id
          }
        ];

        if (this.isAttachmentRequest) {
          const attachmentRequest = await new AttachmentRequest({
            partnerId: this.partnerId,
            title: `${this.$t("objects.attachmentRequest.title").toString()} ${created.claimNumber} ${created.title}`,
            emailAddresses: [created.customerContact.email],
            personIds: created.personId ? [created.personId] : undefined,
            refs: [
              {
                refType: BackendResourceEnum.REPORT,
                refId: created._id
              }
            ],
            screenOrder: this.screens,
            reportId: created.id
          }).create();

          this.$log.debug(attachmentRequest.token);

          createSharedContentDto.refs.push({
            refType: BackendResourceEnum.ATTACHMENT_REQUEST,
            refId: attachmentRequest.id
          });

          submit.attachmentRequest = attachmentRequest;
        }

        const newShared = await createSharedContentDto.create();
        this.$emit("shared", newShared);

        if (newShared) {
          await new ActivityLog({
            partnerId: created.partnerId,
            source: {
              refType: ResourceEnum.REPORT,
              refId: created._id
            },
            target: [{ refType: ResourceEnum.SHARED_CONTENT, refId: newShared.id }],
            actionType: ActionEnum.CREATE,
            activity: ActivityTypeEnum.SHARE
          }).create();
        }

        this.$log.debug(newShared?.url);

        submit.sharedContent = newShared;
      }

      PartnerModule.setReport(created);

      this.$emit("submit", submit);
    } catch (e) {
      handleError(e);
    } finally {
      this.loading = false;
    }
  }

  // Helper computed property to control the Next button
  get canProceedToNextStep() {
    switch (this.currentStep) {
      case CreateReportSteps.VEHICLE:
        return this.stepCompletion.step1;
      case CreateReportSteps.DRIVER:
        return this.stepCompletion.step2 && this.isDriverValid;
      case CreateReportSteps.DAMAGE:
        return this.stepCompletion.step3 && this.isDamageValid;
      default:
        return true;
    }
  }

  // Helper methods for the information panel
  getStepTitle(step: number) {
    switch (step) {
      case 1:
        return this.$t("components.partner.PartnerReportCreateForm.stepTitles.selectVehicle");
      case 2:
        return this.$t("components.partner.PartnerReportCreateForm.stepTitles.selectPerson");
      case 3:
        return this.$t("components.partner.PartnerReportCreateForm.stepTitles.enterDamageDetails");
      case 4:
        return this.$t("components.partner.PartnerReportCreateForm.stepTitles.reviewAndSubmit");
      default:
        return "";
    }
  }

  // Add these computed properties for the transformed items
  get damageTypeItems() {
    return (this.partner.settings?.damageTypes ?? Object.values(DamageTypeEnum)).map((value) => ({
      value,
      text: this.$t(`enums.DamageTypeEnum.${value}`)
    }));
  }

  get coverageTypeItems() {
    return Object.values(CoverageTypeEnum).map((value) => ({
      value,
      text: this.$t(`enums.CoverageTypeEnum.${value}`)
    }));
  }

  serviceIconMap(service: CompanyServiceEnum | undefined) {
    if (!service) {
      return "mdi-domain";
    }

    return CompanyBase.serviceIconMapping.get(service)?.icon;
  }

  // Custom filter function for searching through translated text
  customFilter(item: any, queryText: string) {
    if (!queryText) return true;

    const normalizedText = item.text.toLowerCase();
    const normalizedQuery = queryText.toLowerCase();

    return normalizedText.indexOf(normalizedQuery) > -1;
  }

  goToVehicle() {
    if (this.canReadVehicle) {
      this.selectedVehicle?.goTo(this.$router).detail();
    }
  }

  goToFleet() {
    if (this.canReadFleet) {
      this.selectedFleet?.goTo(this.$router).detail();
    }
  }
}
</script>

<style scoped>
.left-border-column {
  border-left: 1px solid rgba(0, 0, 0, 0.12);
}
</style>
